/**
 * Раздвижная дверь на 2D-чертеже: разрыв в стене, полотно вдоль грани стены и его
 * положение в открытом виде пунктиром, стрелка показывает, куда оно едет.
 *
 * Локальная система координат та же, что у распашной двери: начало — левый край проёма,
 * ось X вдоль стены. flipHinge — в какую сторону вдоль стены едет полотно (по умолчанию
 * влево, к началу проёма), flipSwing — у какой грани стены оно висит (как дуга у распашной).
 */
import type { OpeningShapeProps } from "./DoorShape";

/** Толщина полотна на плане, мм */
const LEAF = 40;
/** Зазор между гранью стены и полотном, мм */
const GAP = 10;
/** Насколько полотно в закрытом виде заходит на стену с каждой стороны проёма, мм */
const OVERLAP = 50;

export const SlidingDoorShape = ({
	opening,
	wallThickness,
	selected,
	mmPerPx,
	interactive,
	onPointerDown,
}: OpeningShapeProps) => {
	const t = wallThickness;
	const w = opening.width;
	const px = (n: number) => n * mmPerPx;
	const accent = selected ? "#2f6feb" : "#39414f";
	const guide = selected ? "#8ab0f5" : "#aab4c4";
	// Грань, у которой висит полотно: по умолчанию «вверх» по оси Y, как дуга распашной
	const face = opening.flipSwing ? 1 : -1;
	const leafY = face > 0 ? t / 2 + GAP : -t / 2 - GAP - LEAF;
	const leafWidth = w + OVERLAP * 2;
	// Куда едет полотно: по умолчанию влево — открытое стоит левее проёма
	const shift = opening.flipHinge ? w : -w;
	const arrowY = leafY + LEAF / 2;
	const arrowFrom = w / 2 + shift * 0.15;
	const arrowTo = w / 2 + shift * 0.45;
	const head = Math.sign(shift) * px(6);

	return (
		<g>
			{/* Разрыв в стене */}
			<rect x={0} y={-t / 2} width={w} height={t} fill="#ffffff" />
			{/* Полотно в открытом виде */}
			<rect
				x={-OVERLAP + shift}
				y={leafY}
				width={leafWidth}
				height={LEAF}
				fill="none"
				stroke={guide}
				strokeWidth={px(1.2)}
				strokeDasharray={`${px(6)} ${px(4)}`}
			/>
			{/* Полотно в закрытом виде */}
			<rect
				x={-OVERLAP}
				y={leafY}
				width={leafWidth}
				height={LEAF}
				fill="#ffffff"
				stroke={accent}
				strokeWidth={px(2)}
			/>
			{/* Стрелка: куда едет полотно */}
			<path
				d={`M ${arrowFrom} ${arrowY} L ${arrowTo} ${arrowY} M ${arrowTo - head} ${arrowY - Math.abs(head) * 0.6} L ${arrowTo} ${arrowY} L ${arrowTo - head} ${arrowY + Math.abs(head) * 0.6}`}
				fill="none"
				stroke={accent}
				strokeWidth={px(1.2)}
			/>
			{/* Откосы по краям проёма */}
			<line x1={0} y1={-t / 2} x2={0} y2={t / 2} stroke={accent} strokeWidth={px(1.5)} />
			<line x1={w} y1={-t / 2} x2={w} y2={t / 2} stroke={accent} strokeWidth={px(1.5)} />
			{/* Невидимая зона захвата для перетаскивания вдоль стены */}
			<rect
				data-role="opening"
				data-id={opening.id}
				x={0}
				y={-t / 2}
				width={w}
				height={t}
				fill="transparent"
				style={{
					cursor: interactive ? "grab" : "default",
					pointerEvents: interactive ? "auto" : "none",
				}}
				onPointerDown={onPointerDown}
			/>
		</g>
	);
};
